import styles from "./DeleteStockWidget.module.scss";

import { useEffect } from "react";
import { motion } from "framer-motion";
import { Controller } from "react-hook-form";
import { useRemoveStock } from "./DeleteStockLogic";
import { useDeleteStockFormValidation } from "./validation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import primaryComponents, { BackButton } from "../../primaryComponents";
import globalStyles from "../../../assets/styles/base/globalPage.module.scss";

const REASON_OPTIONS = [
  { value: "EXPIRED", label: "Expired" },
  { value: "OVERSTOCKED", label: "Overstocked" },
  { value: "DAMAGED", label: "Damaged" },
  { value: "WRONG_ENTRY", label: "Wrong Entry" },
  { value: "OPERATIONAL_USE", label: "Operational Use" },
  { value: "OTHER", label: "Other" },
];

const DeleteStockWidget = ({ closeWidget, draftRequest = null }) => {
  const {
    isDeletingStock,
    isDraftAction,
    formData,
    selectedProductVariants,
    stores,
    images,
    isUploadingImage,
    imageError,
    requiresApproval,
    requiresEvidence,
    activeDraft,
    firstItem,
    isLoadingDraftDetails,
    handleUploadImage,
    handleRemoveImage,
    setStores,
    setFormData,
    setSelectedProductVariants,
    trackedItems,
    handleRemoveStock,
  } = useRemoveStock({ closeWidget, draftRequest });

  const {
    handleSubmit,
    control,
    resetField,
    reset,
    formState: { errors },
  } = useDeleteStockFormValidation(formData);

  useEffect(() => {
    if (activeDraft && firstItem) {
      const storeId =
        activeDraft?.store?.id ||
        activeDraft?.store_id ||
        firstItem?.store_id ||
        "";
      const productId =
        firstItem?.product_id ||
        (typeof firstItem?.product === "object"
          ? firstItem?.product?.id
          : firstItem?.product) ||
        "";

      const variantId =
        firstItem?.variant_id ||
        (typeof firstItem?.variant === "object"
          ? firstItem?.variant?.id
          : firstItem?.variant) ||
        "";

      const expiry = firstItem?.expiry_date
        ? firstItem.expiry_date.split("T")[0]
        : "";

      reset({
        product_id: productId,
        variant_id: variantId,
        store_id: storeId,
        quantity: firstItem?.quantity || 1,
        reason: firstItem?.reason || "",
        description: firstItem?.description || activeDraft?.remarks || "",
        expiry_date: expiry,
        item_ids: firstItem?.item_ids || [],
      });
    } else if (!draftRequest) {
      reset({
        product_id: "",
        variant_id: "",
        store_id: "",
        quantity: 0,
        reason: "",
        description: "",
        expiry_date: "",
        item_ids: [],
      });
    }
  }, [activeDraft, firstItem, draftRequest, reset]);

  const productName =
    firstItem?.product_name ||
    (typeof firstItem?.product === "object"
      ? firstItem?.product?.name || firstItem?.product?.title
      : null) ||
    firstItem?.name ||
    firstItem?.title ||
    "";

  const productId =
    firstItem?.product_id ||
    (typeof firstItem?.product === "object"
      ? firstItem?.product?.id
      : firstItem?.product) ||
    "";

  const draftProduct =
    productId || productName
      ? {
          id: productId,
          name: productName,
          variants:
            typeof firstItem?.product === "object"
              ? firstItem?.product?.variants || []
              : [],
          stores:
            typeof firstItem?.product === "object"
              ? firstItem?.product?.stores || []
              : [],
        }
      : null;

  return (
    <div className={styles.deleteStockWidget}>
      <div>
        <BackButton onClick={closeWidget} />
      </div>

      <h3 className="mt-4">
        {activeDraft ? "Continue Stock Removal Draft" : "Remove Stock"}
      </h3>
      <p>
        {activeDraft
          ? "Update draft details or submit this request for manager approval."
          : "Effortlessly dispose of expired products or adjust overstocked quantities"}
      </p>

      {activeDraft && (
        <div
          style={{
            backgroundColor: "#F7F5FF",
            border: "1px solid #7647EE33",
            borderRadius: "8px",
            padding: "10px 14px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <span
              style={{
                fontSize: "11px",
                color: "#7647EE",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Continuing Draft
            </span>
            <h6
              style={{
                margin: "2px 0 0 0",
                fontSize: "14px",
                fontWeight: "600",
                color: "#1A202C",
              }}
            >
              {activeDraft?.request_id || activeDraft?.id}
            </h6>
          </div>
          <span
            className={`${globalStyles.statusBlock} ${globalStyles["statusBlock--purple"]}`}
            style={{ fontSize: "11px", whiteSpace: "nowrap" }}
          >
            Draft
          </span>
        </div>
      )}

      {isLoadingDraftDetails && !firstItem ? (
        <div className="text-center py-5">
          <FontAwesomeIcon
            icon={["fas", "spinner"]}
            className="fa-spin mr-2 purple--text"
            style={{ fontSize: "24px" }}
          />
          <p className="mt-2 text-muted">Loading draft details...</p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(handleRemoveStock)}
          className={styles.deleteStockForm}
        >
          <div>
            <Controller
              name="product_id"
              control={control}
              defaultValue={formData.product_id}
              render={({ field: { onChange } }) => (
                <primaryComponents.ProductComboBox
                  key={
                    draftProduct
                      ? `draft-${draftProduct.id}-${draftProduct.name}`
                      : "new-product"
                  }
                  label={"Search and select the product you want to remove"}
                  defaultProduct={draftProduct}
                  onChange={(selectedProduct) => {
                    if (selectedProduct) {
                      const {
                        variants,
                        stores: productStores,
                        id,
                      } = selectedProduct;
                      if (variants) {
                        const productVariants = variants.map((variant) => ({
                          label: `${variant.option} - ${variant.value}`,
                          value: variant?.id,
                        }));
                        setSelectedProductVariants(productVariants);
                      } else {
                        setSelectedProductVariants([]);
                        resetField("variant_id", { defaultValue: "" });
                      }
                      if (productStores) {
                        setStores(productStores);
                      }

                      onChange(id);
                      setFormData((prev) => ({ ...prev, product_id: id }));
                    }
                  }}
                  error={errors?.product_id?.message}
                />
              )}
            />
          </div>
          {selectedProductVariants.length ? (
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ delay: 0.25 }}
            >
              <Controller
                name="variant_id"
                control={control}
                defaultValue={formData.variant_id}
                render={({ field: { onChange } }) => (
                  <primaryComponents.SelectField
                    options={selectedProductVariants}
                    isRequired
                    label={
                      "Select The Variant of The Product You Want To Remove"
                    }
                    nameKey="label"
                    idKey="value"
                    value={
                      selectedProductVariants?.find(
                        (v) => `${v?.value}` === `${formData?.variant_id}`,
                      )?.label || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setFormData((prev) => ({
                        ...prev,
                        variant_id: value,
                      }));
                    }}
                    errorMessage={errors.variant_id}
                  />
                )}
              />
            </motion.div>
          ) : null}
          <div>
            <Controller
              name="store_id"
              control={control}
              defaultValue={formData.store_id}
              render={({ field: { onChange } }) => (
                <primaryComponents.SelectField
                  isRequired
                  label={
                    "Select The Store You Want To Remove This Product From"
                  }
                  options={stores}
                  nameKey="name"
                  idKey="id"
                  value={
                    stores?.find(
                      (store) => `${store?.id}` === `${formData?.store_id}`,
                    )?.name ||
                    activeDraft?.store?.name ||
                    ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    setFormData((prev) => ({ ...prev, store_id: value }));
                  }}
                  errorMessage={errors.store_id}
                />
              )}
            />
          </div>

          <Controller
            name="quantity"
            control={control}
            defaultValue={formData.quantity}
            render={({ field: { onChange } }) => (
              <primaryComponents.InputField
                classNames="white"
                isRequired
                label={"Enter The Quantity You Want To Delete"}
                onChange={({ target: { value } }) => {
                  onChange(value);
                  setFormData((prev) => ({ ...prev, quantity: value }));
                }}
                value={formData.quantity}
                errorMessage={errors.quantity}
              />
            )}
          />

          {trackedItems?.length ? (
            <primaryComponents.MultipleSelectField
              label="Select item level tracking number"
              options={trackedItems}
              nameKey={"item_name"}
              idKey={"id"}
              value={formData?.item_ids || []}
              onChange={(values) => {
                let newValues = [];
                values?.forEach((item) => {
                  newValues.push(item.value);
                });
                setFormData((prev) => ({ ...prev, item_ids: newValues }));
              }}
            />
          ) : null}

          <div>
            <Controller
              name="reason"
              control={control}
              defaultValue={formData.reason}
              render={({ field: { onChange } }) => (
                <primaryComponents.SelectField
                  isRequired
                  nameKey="label"
                  idKey="value"
                  label={"Why Do You Want To Delete This Product?"}
                  options={REASON_OPTIONS}
                  value={
                    REASON_OPTIONS.find((r) => r.value === formData?.reason)
                      ?.label || ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    setFormData((prev) => ({ ...prev, reason: value }));
                  }}
                  errorMessage={errors.reason}
                />
              )}
            />
          </div>

          {formData?.reason === "OTHER" ? (
            <div>
              <primaryComponents.InputFieldV2
                control={control}
                classNames="white"
                name="description"
                label="Description"
                placeholder="Enter description"
                value={formData?.description}
                onChange={({ target: { value } }) => {
                  setFormData((prev) => ({
                    ...prev,
                    description: value,
                  }));
                }}
              />
            </div>
          ) : null}

          <div className="mt-3">
            <label
              style={{
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "6px",
                display: "block",
              }}
            >
              Expiration Date (Optional)
            </label>
            <input
              type="date"
              className="form-control"
              style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
                padding: "10px 14px",
                fontSize: "14px",
                width: "100%",
              }}
              value={formData?.expiry_date || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  expiry_date: e.target.value,
                }))
              }
            />
          </div>

          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "0",
                }}
              >
                Evidence Images{" "}
                {requiresEvidence ? (
                  <span style={{ color: "#E02020" }}>* (Required)</span>
                ) : (
                  <span style={{ color: "#718096", fontWeight: "normal" }}>
                    (Optional, max 4)
                  </span>
                )}
              </label>
              <span style={{ fontSize: "12px", color: "#718096" }}>
                {images.length}/4
              </span>
            </div>

            {imageError && (
              <p
                style={{
                  color: "#E02020",
                  fontSize: "12px",
                  marginTop: "4px",
                  marginBottom: "8px",
                }}
              >
                {imageError}
              </p>
            )}

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                marginTop: "8px",
              }}
            >
              {images.map((url, idx) => (
                <div
                  key={idx}
                  style={{
                    position: "relative",
                    width: "72px",
                    height: "72px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  <img
                    src={url}
                    alt={`evidence-${idx}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(idx)}
                    style={{
                      position: "absolute",
                      top: "3px",
                      right: "3px",
                      background: "rgba(0,0,0,0.65)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "10px",
                      padding: 0,
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}

              {images.length < 4 && (
                <label
                  style={{
                    width: "72px",
                    height: "72px",
                    borderRadius: "8px",
                    border: "2px dashed #CBD5E0",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isUploadingImage ? "not-allowed" : "pointer",
                    backgroundColor: "#F7FAFC",
                    margin: 0,
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    disabled={isUploadingImage}
                    onChange={handleUploadImage}
                  />
                  {isUploadingImage ? (
                    <span style={{ fontSize: "11px", color: "#718096" }}>
                      Uploading...
                    </span>
                  ) : (
                    <>
                      <span
                        style={{
                          fontSize: "20px",
                          color: "#A0AEC0",
                          lineHeight: "1",
                        }}
                      >
                        +
                      </span>
                      <span style={{ fontSize: "10px", color: "#718096" }}>
                        Add photo
                      </span>
                    </>
                  )}
                </label>
              )}
            </div>
          </div>

          {requiresApproval && (
            <div className={styles.approvalNotice}>
              <FontAwesomeIcon
                icon="info-circle"
                style={{
                  color: "#7647EE",
                  fontSize: "16px",
                  marginTop: "2px",
                  flexShrink: 0,
                }}
              />
              <div>
                <strong>Approval Required:</strong> This store requires manager
                review. A removal request will be submitted for approval.
              </div>
            </div>
          )}

          <div className={`${globalStyles.stickyBottomCover} mt-1`}>
            {requiresApproval ? (
              <div className={styles.actionButtons}>
                <primaryComponents.Button
                  type="button"
                  classNames="btn btn--primary"
                  isDisabled={isDeletingStock}
                  isLoading={isDeletingStock && !isDraftAction}
                  loadingText="Submitting..."
                  onClick={handleSubmit(() => handleRemoveStock(false))}
                >
                  Submit for Approval
                </primaryComponents.Button>

                <primaryComponents.Button
                  type="button"
                  classNames="btn btn--outline"
                  isDisabled={isDeletingStock}
                  isLoading={isDeletingStock && isDraftAction}
                  loadingText="Saving Draft..."
                  onClick={handleSubmit(() => handleRemoveStock(true))}
                >
                  {activeDraft ? "Update Draft" : "Save as Draft"}
                </primaryComponents.Button>
              </div>
            ) : (
              <primaryComponents.Button
                type="button"
                classNames="btn btn--danger cover"
                isDisabled={isDeletingStock}
                isLoading={isDeletingStock}
                loadingText="Removing..."
                onClick={handleSubmit(() => handleRemoveStock(false))}
              >
                Submit
              </primaryComponents.Button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default DeleteStockWidget;
